import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { authAPI } from '../services/authAPI'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import { ArrowLeft, Eye, EyeOff, CheckCircle, AlertCircle, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import AuthShell from '../components/Layout/AuthShell'

export default function ResetPassword() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [tokenValid, setTokenValid] = useState(false)

  useEffect(() => {
    validateToken()
  }, [token])

  const validateToken = async () => {
    if (!token) {
      setError('Invalid reset token')
      setValidating(false)
      return
    }

    try {
      // You might want to add a token validation endpoint
      // For now, we'll assume the token is valid if it exists
      setTokenValid(true)
    } catch (error) {
      setError('Invalid or expired reset token')
      toast.error('Invalid or expired reset token')
    } finally {
      setValidating(false)
    }
  }

  // Common weak passwords
  const weakPasswords = [
    'password', 'password123', 'password1',
    '123456', '12345678', '123456789', '1234567890'
  ]

  // Check individual password requirements
  const checkPasswordRequirements = (password) => {
    const lowerPassword = password.toLowerCase()

    return {
      length: password.length >= 8 && password.length <= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      notWeak: !weakPasswords.some(weak => lowerPassword === weak || lowerPassword.includes(weak))
    }
  }

  // Get password requirements status for real-time feedback
  const passwordRequirements = useMemo(() => {
    if (!formData.password) return null
    return checkPasswordRequirements(formData.password)
  }, [formData.password])

  // Check if all requirements are met
  const allRequirementsMet = useMemo(() => {
    if (!passwordRequirements) return false
    return Object.values(passwordRequirements).every(req => req === true)
  }, [passwordRequirements])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (error) setError(null)
  }

  const validatePassword = (password) => {
    const errors = []
    const requirements = checkPasswordRequirements(password)

    // Check length (8-12 characters)
    if (!requirements.length) {
      if (password.length < 8) {
        errors.push('Password must be at least 8 characters')
      } else if (password.length > 12) {
        errors.push('Password must be no more than 12 characters')
      }
    }

    // Check for uppercase letter
    if (!requirements.uppercase) {
      errors.push('Password must contain at least 1 uppercase letter')
    }

    // Check for lowercase letter
    if (!requirements.lowercase) {
      errors.push('Password must contain at least 1 lowercase letter')
    }

    // Check for number
    if (!requirements.number) {
      errors.push('Password must contain at least 1 number')
    }

    // Check for special character
    if (!requirements.specialChar) {
      errors.push('Password must contain at least 1 special character')
    }

    // Check for common/weak passwords (case-insensitive)
    if (!requirements.notWeak) {
      errors.push('Password is too common or weak. Please choose a stronger password')
    }

    return errors.length > 0 ? errors[0] : null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    
    // Validation
    if (!formData.password) {
      setError('Password is required')
      return
    }
    
    if (!formData.confirmPassword) {
      setError('Please confirm your password')
      return
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    const passwordError = validatePassword(formData.password)
    if (passwordError) {
      setError(passwordError)
      return
    }
    
    setLoading(true)
    try {
      await authAPI.resetPassword(token, formData.password)
      setSuccess(true)
      toast.success('Password reset successfully!')
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { replace: true })
      }, 3000)
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to reset password. Please try again.'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (validating) {
    return (
      <AuthShell title="Reset password" subtitle="Checking your reset link...">
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      </AuthShell>
    )
  }

  if (!tokenValid) {
    return (
      <AuthShell title="Link expired" subtitle="This password reset link is invalid or has expired.">
        <div className="space-y-5">
          <div className="flex gap-3 rounded-xl border border-error-200 bg-error-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-error-500" />
            <p className="text-sm text-error-700">{error || 'This password reset link is invalid or has expired.'}</p>
          </div>
          <Link to="/forgot-password" className="auth-link inline-flex items-center gap-1">
            Request a new reset link
          </Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title={success ? 'Password updated' : 'Create a new password'}
      subtitle={
        success
          ? 'You can now sign in with your new password.'
          : 'Choose a strong password for your account.'
      }
    >
      {success ? (
        <div className="flex gap-3 rounded-xl border border-primary-200 bg-primary-50 p-4">
          <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
          <div>
            <p className="text-sm font-medium text-primary-900">Password reset successful</p>
            <p className="mt-1 text-sm text-primary-700">Redirecting you to sign in...</p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="password" className="auth-field">
              New password
            </label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                placeholder="Enter a new password"
                value={formData.password}
                onChange={handleInputChange}
                className={`auth-input w-full pr-11 ${error ? 'border-error-500' : ''}`}
                disabled={loading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-secondary-400 hover:text-secondary-600"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {formData.password && passwordRequirements && !allRequirementsMet && (
              <div className="mt-2 rounded-xl border border-secondary-200 bg-white p-3">
                <p className="mb-2 text-xs font-medium text-secondary-700">Password requirements</p>
                <ul className="space-y-1 text-xs">
                  {[
                    [passwordRequirements.length, '8-12 characters'],
                    [passwordRequirements.uppercase, 'At least 1 uppercase letter'],
                    [passwordRequirements.lowercase, 'At least 1 lowercase letter'],
                    [passwordRequirements.number, 'At least 1 number'],
                    [passwordRequirements.specialChar, 'At least 1 special character'],
                    [passwordRequirements.notWeak, 'Not a common/weak password']
                  ].map(([ok, label]) => (
                    <li key={label} className={`flex items-center ${ok ? 'text-success-600' : 'text-secondary-500'}`}>
                      {ok ? <Check className="mr-2 h-3 w-3" /> : <span className="mr-2 w-3">-</span>}
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="auth-field">
              Confirm password
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                placeholder="Confirm your new password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`auth-input w-full pr-11 ${error ? 'border-error-500' : ''}`}
                disabled={loading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-secondary-400 hover:text-secondary-600"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-xl border border-error-200 bg-error-50 px-3 py-2.5">
              <p className="text-sm text-error-600">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            className="h-12 w-full rounded-xl text-[15px] font-semibold"
            loading={loading}
            disabled={loading}
          >
            Reset password
          </Button>
        </form>
      )}

      <div className="mt-6">
        <Link to="/login" className="auth-link inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" />
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  )
}
